/**
 * The REAL size of the window, in CSS pixels, on a device whose JS viewport
 * APIs are wrong.
 *
 * ⚠️ WEBKIT #254868 - https://bugs.webkit.org/show_bug.cgi?id=254868
 * "Incorrect height values when viewport-fit=cover is set for installed web
 * apps". NEW since 2023-04, still open on iOS 18.x.
 *
 * On an INSTALLED iOS web app with `viewport-fit=cover` plus
 * `apple-mobile-web-app-status-bar-style: black-translucent`, WebKit paints
 * the page from y=0 - the top really is full-bleed under the status bar - but
 * computes the window HEIGHT as if the page had been inset below it. The
 * shortfall therefore lands at the BOTTOM, outside the paintable surface,
 * where the only thing drawn is the propagated root background colour.
 * `position: fixed; bottom: 0` stops short of the screen with it.
 *
 * MEASURED on the operator's iPad 10.2 in landscape: the screen is 810pt and
 * `window.innerHeight`, `document.documentElement.clientHeight`,
 * `visualViewport.height`, `100dvh` and `100svh` ALL report 778. They AGREE.
 * That is the whole reason five rounds of swapping one API for another moved
 * the band exactly zero points - this was never a question of which one lies,
 * and every instrument inside the document is downstream of the same wrong
 * number. It was diagnosed off a screenshot: the body's own
 * `position: fixed; top: 0; bottom: 0` edge rails stopped at 778 too.
 *
 * `100vh` is the LARGE viewport, and per the bug's own comments it is the one
 * unit the inset does not corrupt: it read 810 on that device where everything
 * else read 778. So this module CAN tell you the true screen height.
 *
 * ⚠️ KNOWING THE SCREEN IS 810 IS ONLY HALF THE FIX. The page must also be
 * allowed to be 810 tall: `html, body { height: 100% }` resolves against the
 * SHORT viewport, and with `overflow: hidden` on it the difference is clipped
 * away whatever this function returns. Both halves live together or neither
 * works - see the note above the `html, body` rule in `app.css`.
 *
 * ⚠️ NOT VERIFIABLE OFF THE DEVICE (CP-070/072). On this machine, and in every
 * headless browser, `100vh` and `clientHeight` are the same number, so no local
 * test can tell any of this apart. Both numbers are published in the Navigate
 * frame readout, which is how the device answered it in one launch instead of
 * another round of inference - keep them there.
 */

/** TEST ONLY, mirrors `autoFit.ts`. The standalone check cannot be emulated by
 *  Playwright, and an untestable guard is one nobody can prove works. The bot
 *  seeds this; nothing in the app ever writes it. */
const ASSUME_STANDALONE_KEY = 'opbuttons.autofit.assumeStandalone';

/** True when running as an installed app rather than a browser tab.
 *
 *  ONE implementation, imported by `autoFit.ts` rather than copied into it -
 *  two copies of this predicate is how one of them quietly stops matching the
 *  other. Both `display-mode: standalone` and `navigator.standalone` are
 *  checked; the second is the belt-and-braces one on iOS. */
export function isStandalone(): boolean {
  try {
    if (localStorage.getItem(ASSUME_STANDALONE_KEY) === '1') return true;
  } catch { /* private mode */ }
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  } catch { /* older webviews */ }
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

let probe: HTMLDivElement | undefined;

/** `100vh` resolved to pixels, by measuring an element that is actually laid
 *  out at it. There is no way to ask CSS for this directly.
 *
 *  `position: fixed` so no ancestor's height can constrain it; zero width,
 *  `visibility: hidden` and `pointer-events: none` so it can never be seen,
 *  hit-tested or affect layout. Height is the only thing being asked of it.
 *  The element is created once and reused - it re-resolves `100vh` on every
 *  read, so there is nothing to keep in sync. */
export function viewportUnitHeight(): number {
  if (typeof document === 'undefined') return 0;
  if (!probe || !probe.isConnected) {
    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:100vh;'
      + 'visibility:hidden;pointer-events:none;margin:0;padding:0;border:0;';
    document.body.appendChild(probe);
  }
  return probe.getBoundingClientRect().height;
}

export interface ViewportBox {
  w: number;
  h: number;
  /** What `documentElement.clientHeight` reports: the LAYOUT viewport, short
   *  by the #254868 inset on an installed iOS app. Published in the readout. */
  layoutH: number;
  /** What `100vh` measured: the true SCREEN height, and what `h` follows in an
   *  installed app. Published in the readout beside `layoutH` - the pair is
   *  what made this diagnosable at all. */
  unitH: number;
  standalone: boolean;
}

/**
 * The box to size the shell into: the TRUE screen height in an installed app.
 *
 * ⚠️ THE HISTORY MATTERS HERE, because this has been changed in both
 * directions and the middle attempt looked like proof that it could not work.
 *
 * `100vh` reports the true screen height - 810 on the operator's iPad where
 * `innerHeight`, `clientHeight`, `visualViewport`, `100dvh` and `100svh` all
 * say 778 (WebKit #254868). Sizing the shell to 810 was tried and the app did
 * adopt it: the on-device readout said `usable 1080x810 - drawn for 1080x810 -
 * 100%`. The band did not move, the full-bleed Navigate modal came back 0pt
 * inset at the sides and 32pt short at the bottom, and that was read as "the
 * compositor will not paint below 778, the points are unrecoverable".
 *
 * WRONG, and wrong in the most expensive way available: it was OUR OWN CLIP.
 * `app.css` had `html, body { height: 100%; overflow: hidden }`, `height: 100%`
 * resolves against the SHORT layout viewport, so body was 778 and cut the box
 * off at 778 no matter what size the box was. The measurement was never the
 * thing being tested. The two must move together - see the note above the
 * `html, body` rule - and the tell that the surface was paintable all along
 * was sitting in the screenshot: the band paints `--color-canvas`, the PAGE's
 * colour propagated across the canvas, not the manifest's `background_color`.
 *
 * `max` rather than simply preferring `unitH`: on every device without this
 * bug the two are equal, so the result is unchanged, and this can never return
 * a box smaller than the layout viewport. It cannot regress a rig that is
 * currently correct, which matters more than elegance on a surface that gets
 * gigged.
 *
 * Width is deliberately untouched. The inset in #254868 is vertical, the
 * measured width has been correct in every screenshot, and `100vw` has its own
 * well-known scrollbar problem off iOS. Narrow change.
 */
/** The raw `env(safe-area-inset-*)` values in px. `env()` cannot be read from
 *  JS directly, so an invisible element is laid out padded by it and read.
 *  Used by the frame report (Navigate > Details) and by `maybeRecordReserve`,
 *  one implementation so the two can never disagree. */
export function safeAreaInsets(): { top: number; right: number; bottom: number; left: number } {
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);';
  document.body.appendChild(el);
  const cs = getComputedStyle(el);
  const n = (v: string) => { const x = parseFloat(v); return Number.isFinite(x) ? Math.round(x * 100) / 100 : 0; };
  const out = { top: n(cs.paddingTop), right: n(cs.paddingRight), bottom: n(cs.paddingBottom), left: n(cs.paddingLeft) };
  el.remove();
  return out;
}

export function measureViewportBox(): ViewportBox | null {
  const de = document.documentElement;
  const w = Math.round(de.clientWidth || window.innerWidth);
  const layoutH = Math.round(de.clientHeight || window.innerHeight);
  const standalone = isStandalone();
  const unitH = Math.round(viewportUnitHeight());
  const h = standalone && unitH > 0 ? Math.max(layoutH, unitH) : layoutH;
  if (!(w > 0 && h > 0)) return null;
  return { w, h, layoutH, unitH, standalone };
}
